import i18n from 'i18next';
import type { i18n as I18nInstance, Resource } from 'i18next';
import { setDefaultOptions } from 'date-fns';
import sprintf from 'i18next-sprintf-postprocessor';
import languageDetector from 'i18next-browser-languagedetector';
import dateLocales from '../constants/dateLocales';

/*
 * i18n Initialization singleton
 *
 * @param resources object
 *
 * A static Object passed per-application that encompasses language, namespace, and translations.
 * Example:
 * {
 *  en: {
 *  exampleNamesapce: require('locales/en/translations.json')
 *  },
 *  ...
 * }
 */
export const i18nInstance = (
  resources?: Resource,
  namespace = 'defaultNamespace'
): { getInstance: () => I18nInstance } => {
  return (function (resources?: Resource, namespace?: string) {
    let instance: I18nInstance | null = null;

    function createInstance(resources?: Resource, namespace?: string): I18nInstance {
      // cast: window.i18nDashiFi is an untyped host-page global escape hatch
      return (typeof window !== 'undefined' && (window as any).i18nDashiFi) || setup_i18n(resources, namespace);
    }
    return {
      getInstance: function () {
        if (!instance) instance = createInstance(resources, namespace);
        return instance;
      },
    };
    function setup_i18n(resources?: Resource, namespace?: string): I18nInstance {
      const newInstance = i18n.createInstance();
      newInstance
        .use(sprintf)
        .use(languageDetector)
        .init({
          resources: {},
          partialBundledLanguages: true,
          fallbackLng: ['en'],
          fallbackNS: ['defaultNamespace'],
          ns: [namespace as string],
          defaultNS: namespace,
          debug: false,
          keySeparator: '>',
          nsSeparator: '|',
          returnObjects: true,
          returnEmptyString: false,
          interpolation: {
            escapeValue: false,
            formatSeparator: ',',
            // any: i18next passes arbitrary interpolation values through this formatter
            format: function (value: any, format?: string) {
              if (format === 'uppercase') return value.toUpperCase();
              return value;
            },
          },
          detection: {
            order: ['querystring', 'htmlTag', 'cookie', 'navigator'], // order and from where user language should be detected
            lookupQuerystring: 'locale', // keys or params to lookup language from
            lookupCookie: 'locale',
          },
        });
      // cast: language is a runtime-detected string key; dateLocales has a fixed key set (en fallback preserved)
      setDefaultOptions({ locale: dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en });
      return newInstance;
    }
  })(resources, namespace);
};
